import React, { useEffect, useState } from "react"
import notFound from "../../containers/Contacts/Table/NotFoundData"
import DataGridSkeletonOverlay from "../Skeleton/DataGridSkeletonOverlay"
import { DataGrid, useGridApiRef } from "@mui/x-data-grid"
import { styled } from "@mui/material/styles"
import Button from "../Button/Button"
import { formatFullDate, getSlashDateFormat } from "../../utils/date"
import ConfirmModal from "../ConfirmModal/ConfirmModal"
import { useDispatch } from "react-redux"
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider"
import { showSnackBar } from "../Snackbar/Snackbar"
import { deleteTaskByIdReq, getTaskStatuses, updateTaskReq } from "../../features/tasks/tasks"
import { NavLink } from "react-router-dom"
import { handleTableId } from "../../helpers/helper"
import StatusBadge from "../StatusBadge/StatusBadge"
import { LuPencil, LuTrash2 } from "react-icons/lu"
import TaskChip, { ReminderStatusChip } from "./TaskChip"
import { useTaskFlags } from "./useTaskFlags"
import { canDeleteTaskRow, canEditTaskRow, formatReminderTime, isTaskOverdue } from "./taskReminders"
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog"
import { useUiKit } from "../UiKit/UiKitContext"
import { failureOf } from "../../utils/errorHelpers"

// Local styled component removed in favor of global theme overrides

const TasksTabTable = ({ data, setActiveTab, setTaskId, setIsUpdated, isUpdated, hideActions = false }) => {
  const apiRef = useGridApiRef()
  const [openDeleteTaskModal, setOpenDeleteTaskModal] = useState(false)
  const [statuses, setStatuses] = useState([])
  const dispatch = useDispatch()
  const { triggerLoading, isBarLoading } = useMyContextLoading()
  const { confirm: kitConfirm } = useUiKit()
  const { reminderUiOn, affiliateTasksOn, affiliateRules, viewer, viewerReady } = useTaskFlags()
  const actionsPending = affiliateTasksOn && !viewerReady
  const cannotEdit = (row) => actionsPending || (affiliateRules && !canEditTaskRow(row, viewer?._id))

  const deleteTask = async (taskId) => {
    triggerLoading(true)
    try {
      const response = await dispatch(deleteTaskByIdReq(taskId))
      setIsUpdated(!isUpdated)
      showSnackBar("success", "Task successfully deleted")
      setActiveTab("tasks")
    } catch (error) {
      showSnackBar("error", "Failed to delete task")
    }
    triggerLoading(false)
  }

  const deleteTaskOrThrow = async (taskId) => {
    const response = await dispatch(deleteTaskByIdReq(taskId))
    if (response?.error) {
      throw failureOf(response, "Failed to delete task")
    }
    setIsUpdated(!isUpdated)
    showSnackBar("success", "Task successfully deleted")
    setActiveTab("tasks")
  }

  const getTasksStatus = async () => {
    try {
      const response = await dispatch(getTaskStatuses())
      setStatuses(response?.payload?.data)
    } catch (error) {
      showSnackBar("error", "Failed to fetch task statuses")
    }
  }

  const updateTask = async (taskId) => {
    triggerLoading(true)
    try {
      const selectedId = statuses.find((e) => e?.label === "Complete")?.value
      const data = {
        status: selectedId
      }

      const response = await dispatch(updateTaskReq({ taskId, data }))
      if (response?.payload?.data?._id) {
        showSnackBar("success", "Task updated successfully")
      } else {
        showSnackBar("error", "Something going wrong, try later")
      }
      setIsUpdated(!isUpdated)
    } catch (error) {
      showSnackBar("error", "Failed to update task")
    }
    triggerLoading(false)
  }

  useEffect(() => {
    getTasksStatus()
  }, [])

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      sortable: false,
      flex: 1
    },
    {
      field: 'contactId',
      headerName: 'Contact Id',
      renderCell: (params) => {
        return (
          <NavLink
            to={`/contact/${params?.row?.user}`}
            state={{ openTab: "tasks" }}
            className="text-primary-main font-semibold cursor-pointer"
          >
            {handleTableId(params?.row?.user)}
          </NavLink>
        )
      },
      sortable: false,
      // Bounded id link: fixed width clears the uppercase "CONTACT ID" header
      width: 140
    },
    {
      "field": "title",
      "headerName": "Title",
      sortable: false,
      // Flex column: minWidth guards the free-text title from the 50px flex floor when crowded
      flex: 1,
      minWidth: 180
    },
    {
      "field": "status",
      "headerName": "Status",
      renderCell: (params) => {
        return (
          <StatusBadge
            label={params?.row?.status?.label}
            bgColor={params?.row?.status?.bgColor}
            textColor={params?.row?.status?.textColor}
          />
        )
      },
      sortable: false,
      // Bounded badge: fixed width clears the uppercase "STATUS" header
      width: 130
    },
    {
      field: 'due',
      headerName: 'Due',
      renderCell: (params) => {
        if (reminderUiOn && isTaskOverdue(params?.row)) {
          return (
            <div className="flex flex-col items-start gap-1">
              {formatFullDate(params?.row?.due)}
              <TaskChip tone="error">Overdue</TaskChip>
            </div>
          )
        }
        return (
          <div>
            {formatFullDate(params?.row?.due)}
          </div>
        )
      },
      sortable: false,
      // Bounded date (MM/DD/YYYY): fixed width clears the uppercase "DUE" header
      width: 130
    },
    ...(reminderUiOn ? [{
      field: 'reminder',
      headerName: 'Reminder',
      renderCell: (params) => {
        const reminder = params?.row?.reminder
        if (!reminder?.at) return null
        return (
          <div className="flex flex-col items-start gap-1">
            <span>{formatReminderTime(reminder.at)}</span>
            <ReminderStatusChip status={reminder.status} />
          </div>
        )
      },
      sortable: false,
      width: 190
    }] : []),
    {
      "field": "assignedDate",
      "headerName": "Assigned Date",
      renderCell: (params) => {
        return (
          <div>
            {params?.row?.assignedDate ? formatFullDate(params?.row?.assignedDate) : ''}
          </div>
        )
      },
      sortable: false,
      // Bounded date: fixed width clears the wide uppercase "ASSIGNED DATE" header
      width: 150
    },
    {
      "field": "createdDate",
      "headerName": "Created Date",
      renderCell: (params) => {
        return (
          <div>
            {formatFullDate(params?.row?.createdAt)}
          </div>
        )
      },
      sortable: false,
      // Bounded date: fixed width clears the wide uppercase "CREATED DATE" header
      width: 150
    },
    {
      "field": "assignedTo",
      "headerName": "Assigned To",
      renderCell: (params) => {
        return (
          <div>{params?.row?.assignedToData?.firstName} {params?.row?.assignedToData?.lastName}</div>
        )
      },
      sortable: false,
      // Flex column: minWidth guards the variable-length name from the 50px flex floor when crowded
      flex: 1,
      minWidth: 160
    },
    {
      "field": "completedDate",
      "headerName": "Completed Date",
      renderCell: (params) => {
        return (
          params?.row?.status?.value === "complete" ? getSlashDateFormat(params?.row?.completedDate, false) : (
            <Button classes="!bg-success-main" hover="hover:!bg-success-main/90" disabled={hideActions || cannotEdit(params?.row)}
              onClick={() => updateTask(params?.row?._id)}>Complete</Button>
          )
        )
      },
      sortable: false,
      // Bounded date / Complete button: fixed width clears the wide uppercase "COMPLETED DATE" header
      width: 170
    },
  ]

  if (!hideActions) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      renderCell: (params) => {
        if (actionsPending) return null
        const removeFunc = () => deleteTask(params?.row?._id)
        const canEdit = !affiliateRules || canEditTaskRow(params?.row, viewer?._id)
        const canDelete = !affiliateRules || canDeleteTaskRow(params?.row, viewer?._id)
        return (
          <div key={params?.row?.id} className="flex justify-between w-full pr-[6px]">
            {canEdit && (
              <div className="cursor-pointer" onClick={() => {
                setActiveTab("editTask")
                setTaskId(params?.row?._id)
              }}>
                <LuPencil size={18} className="text-text-secondary" />
              </div>
            )}
            {canDelete && (
              <div className="cursor-pointer" onClick={() => setOpenDeleteTaskModal(params?.row?._id)}>
                <LuTrash2 size={18} className="text-text-secondary" />
              </div>
            )}
            {openDeleteTaskModal === params?.row?._id && (kitConfirm ? (
              <ConfirmDialog
                open={Boolean(openDeleteTaskModal)}
                onClose={() => setOpenDeleteTaskModal(false)}
                title={params?.row?.title ? `Delete the task "${params.row.title}"?` : "Delete this task?"}
                impact={["The task is removed from the task list.", "This cannot be undone."]}
                confirmLabel="Delete task"
                tone="danger"
                onConfirm={() => deleteTaskOrThrow(params?.row?._id)}
              />
            ) : (
              <ConfirmModal
                leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
                title="Are you sure to delete the Task?"
                modalButtonTitle={"Delete"}
                classes=""
                open={openDeleteTaskModal}
                setOpen={setOpenDeleteTaskModal}
                functionButton={removeFunc}
              >
                You will not be able to restore it later
              </ConfirmModal>
            ))}
          </div>
        )
      },
      // Icon-only actions: fixed width clears the uppercase "ACTION" header (flex + moot maxWidth dropped)
      width: 90,
      sortable: false
    })
  }


  const getRowId = (row) => row._id
  return (
    <div>
      <DataGrid
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            '.MuiButtonBase-root[aria-haspopup="menu"]': {
              display: 'none'
            }
          },
          '.MuiDataGrid-virtualScroller': {
            minHeight: data?.length === 0 ? 300 : ''
          }
        }}
        rowHeight={75}
        columns={columns}
        slots={{
          noRowsOverlay: notFound,
          loadingOverlay: DataGridSkeletonOverlay,
        }}
        loading={isBarLoading && !(data?.length > 0)}
        getRowId={getRowId}
        columnVisibilityModel={{
          id: false
        }}
        rows={data}
        apiRef={apiRef}
        hideFooterPagination={true}
        checkboxSelection={false}
        disableRowSelectionOnClick
        disableColumnSelector
      />

    </div>
  )
}

export default TasksTabTable